<template>
  <div class="main-pagination">
    <div class="main-pagination__container">
      <div class="main-pagination__control main-pagination__control--prev">
        <template v-if="prevLink">
          <component
            :is="link ? 'BHLink' : 'button'"
            class="main-pagination__control-link"
            :href="link ? prevLink.href : undefined"
            @click="onClickItem(pageCurrent - 1)"
          />
        </template>
      </div>

      <div class="main-pagination__links">
        <component
          :is="item.component"
          v-for="item in paginationItems"
          :key="item.page + clientIndex"
          aria-label="Перейти на предыдущую страницу"
          class="main-pagination__links-item"
          :class="getClassPaginationItem(item)"
          v-bind="getPropsPaginationItem(item)"
          :href="item.component === 'BHLink' ? getLinkPage(item.page)?.href : null"
          @click="onClickItem(item.page)"
        >
          {{ item.page }}
        </component>
      </div>

      <div class="main-pagination__control main-pagination__control--next">
        <template v-if="nextLink">
          <component
            :is="link ? 'BHLink' : 'button'"
            class="main-pagination__control-link"
            aria-label="Перейти на следующую страницу"
            :href="link ? nextLink.href : undefined"
            @click="onClickItem(pageCurrent + 1)"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import BHLink from '~/components/BHLink.vue';
  import { useWindowStore } from '~/store/window';
  import type { IPaginationItem } from '~/types/common';

  interface ILinkProps {
    href: string;
  }

  interface ISchemeItems extends Array<any> {
    [index: number]: number | '...';
  }

  interface ISchemesPagination {
    default: ISchemeItems;
    start: ISchemeItems;
    center: ISchemeItems;
    end: ISchemeItems;
  }

  interface ISettings {
    desktop: {
      offset: number;
      schemes: ISchemesPagination;
    };
    mobile: {
      offset: number;
      schemes: ISchemesPagination;
    };
  }

  export default defineNuxtComponent({
    name: 'MainPagination',

    components: {
      BHLink,
    },

    inject: {
      postfixUrl: {
        default() {
          return () => '';
        },
      },
    },

    props: {
      pageCurrent: {
        type: Number as PropType<number>,
        required: true,
      },

      pagesTotal: {
        type: Number as PropType<number>,
        default: null,
      },

      link: {
        type: Object as PropType<HTMLAnchorElement>,
        default: null,
      },

      pageUrl: {
        type: String as PropType<string>,
        default: 'page',
      },

      isFirstPage: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      hasNextPage: {
        type: Boolean as PropType<boolean>,
        default: null,
      },
    },

    data: () => ({
      clientIndex: 0,
      settings: {
        desktop: {
          offset: 5,
          schemes: {
            default: [9],
            start: [7, '...', 1],
            center: [1, '...', 5, '...', 1],
            end: [1, '...', 7],
          },
        },

        mobile: {
          offset: 2,
          schemes: {
            default: [5],
            start: [3, '...', 1],
            center: [1, '...', 1, '...', 1],
            end: [1, '...', 3],
          },
        },
      } as ISettings,
    }),

    computed: {
      isMobile(): boolean {
        return useWindowStore().isMobile;
      },

      getPostfixUrl(): string {
        if (this.postfixUrl) {
          // @ts-expect-error injected functions are not typed in options API
          return this.postfixUrl();
        } else {
          return '';
        }
      },

      scheme(): ISchemeItems {
        const { pagesTotal, pageCurrent } = this;

        // if (!pagesTotal) return [1];

        const settingsCurrent = this.isMobile
          ? JSON.parse(JSON.stringify(this.settings.mobile))
          : JSON.parse(JSON.stringify(this.settings.desktop));

        const { offset, schemes } = settingsCurrent;

        const countPagesForDefaultScheme = schemes.default[0];

        if (pagesTotal <= countPagesForDefaultScheme) {
          schemes.default = [pagesTotal];
          return schemes.default;
        }

        if (pageCurrent <= offset) {
          return schemes.start;
        } else if (pageCurrent >= pagesTotal - offset) {
          return schemes.end;
        } else {
          return schemes.center;
        }
      },

      schemeItems(): ISchemeItems {
        const { scheme, getRange, pagesTotal, pageCurrent } = this;

        if (!pagesTotal) return [pageCurrent];

        return scheme.reduce(
          (
            result: ISchemeItems,
            item: number | '...',
            index: number,
            self: ISchemeItems,
          ): ISchemeItems => {
            const isFirst = index === 0;
            const isLast = self.length - 1 === index;

            if (typeof item === 'number') {
              let chunk: Array<number> = [];

              if (isFirst) {
                chunk = getRange(1, item);
              } else if (isLast) {
                chunk = getRange(pagesTotal - item + 1, pagesTotal);
              } else {
                const diff = Math.round((item - 1) / 2);
                chunk = getRange(pageCurrent - diff, pageCurrent + diff);
              }

              result.push(...chunk);
            } else {
              result.push(item);
            }

            return result;
          },
          [],
        );
      },

      paginationItems(): Array<IPaginationItem> {
        const { pageCurrent, schemeItems } = this;

        const getItems = (component: 'button' | 'BHLink'): Array<IPaginationItem> => {
          return schemeItems.map((page: number) => ({
            page,
            component: typeof page === 'number' && page !== pageCurrent ? component : 'span',
          }));
        };

        return this.link ? getItems('BHLink') : getItems('button');
      },

      prevLink(): HTMLAnchorElement | null {
        const { pageCurrent, getLinkPage } = this;

        return pageCurrent - 1 ? getLinkPage(pageCurrent - 1) : null;
      },

      nextLink(): HTMLAnchorElement | null {
        const { pageCurrent, pagesTotal, getLinkPage, hasNextPage } = this;

        if (hasNextPage) return getLinkPage(pageCurrent + 1);

        return pagesTotal && pageCurrent !== pagesTotal ? getLinkPage(pageCurrent + 1) : null;
      },
    },

    mounted() {
      this.clientIndex = 9999;
    },

    methods: {
      getLinkPage(page: number): HTMLAnchorElement {
        const { link, isFirstPage, pageUrl, getPostfixUrl } = this;

        const url = link?.href || '';
        let href = null;
        if (page === 1 && !isFirstPage) {
          // проверка, чтобы не вернуться обратно на главную страницу
          href = url === '/' ? `/${pageUrl}/${page}` : url;
        } else {
          href = `${url === '/' ? '' : url}/${pageUrl}/${page}`;
        }

        return {
          ...link,
          href:
            link?.href === '/search' || link?.href === '/'
              ? `${href}${getPostfixUrl}`
              : `${href}${getPostfixUrl}/`,
        };
      },

      getPropsPaginationItem(paginationItem: IPaginationItem): ILinkProps | null {
        return paginationItem.component === 'BHLink'
          ? { href: this.getLinkPage(paginationItem.page).href }
          : null;
      },

      getClassPaginationItem(paginationItem: IPaginationItem): {
        [key: string]: boolean;
      } {
        const { pageCurrent } = this;

        return {
          'main-pagination__links-item--current': paginationItem.page === pageCurrent,

          'main-pagination__links-item--link': typeof paginationItem.page === 'number',
        };
      },

      getRange(start: number, end: number): number[] {
        return Array(end - start + 1)
          .fill(null)
          .map((_, i) => i + start);
      },

      // Функция аналитики элемента пагинации
      onClickItem(pageNumber: number) {
        if (typeof pageNumber !== 'number') return;
        this.$emit('page-link-click', pageNumber);
      },
    },
  });
</script>

<style lang="scss" scoped>
  .main-pagination {
    &__container {
      display: flex;
      justify-content: space-between;
    }

    &__links {
      display: flex;
      align-items: center;

      &-item {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 40px;
        height: 20px;
        border-radius: 50%;
        border: 1px solid transparent;
        cursor: default;

        font-family: $text-font--1;
        @include smallText;
        font-weight: 500;
        color: $icon-color--1;

        & + & {
          margin-left: 10px;
          @include tablet {
            margin-left: $spacing-4;
          }
        }

        @include hover {
          color: $mainBlack;
        }

        &--link {
          cursor: pointer;
        }

        &--current {
          border-color: $active-color--1;
          cursor: default;
          color: #000;

          @include hover {
            color: $icon-color--1;
          }
        }
      }
    }

    &__control {
      width: 24px;
      height: 24px;
      flex-shrink: 0;

      &--next {
        transform: rotate(180deg);
      }

      &-link {
        cursor: pointer;
        display: block;
        width: 100%;
        height: 100%;
        @include arrowIcon;
        background-color: #969698;

        @include hover {
          background-color: $mainBlack;
        }
      }
    }
  }
</style>
